import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, RefreshCw } from "lucide-react";
import { apiRequest, downloadFile } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-amber-100 text-amber-800",
  delete: "bg-rose-100 text-rose-800",
  sign: "bg-blue-100 text-blue-800",
  approve: "bg-purple-100 text-purple-800",
  eidas_prepare: "bg-indigo-100 text-indigo-800",
  eidas_finalize: "bg-indigo-200 text-indigo-900",
  import_csv: "bg-cyan-100 text-cyan-800",
  export: "bg-slate-200 text-slate-800",
  refresh: "bg-cyan-100 text-cyan-800",
  trigger: "bg-fuchsia-100 text-fuchsia-800",
};

export default function AuditLogs() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    action: "", entity_type: "", user_id: "", company_id: "",
    from_date: "", to_date: "",
  });
  const [loading, setLoading] = useState(true);

  const fetchLogs = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append("limit", "300");
    apiRequest(`/audit-logs?${params.toString()}`)
      .then((data) => setLogs(data || []))
      .catch((err) => toast.error("Errore caricamento audit log", { description: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      await downloadFile(`/audit-logs/export?${params.toString()}`, "audit_logs.csv");
      toast.success("CSV audit log scaricato");
    } catch (err) {
      toast.error("Export fallito", { description: err.message });
    }
  };

  const resetFilters = () => {
    setFilters({ action: "", entity_type: "", user_id: "", company_id: "", from_date: "", to_date: "" });
  };

  const fmt = (iso) => iso ? iso.slice(0, 19).replace("T", " ") : "—";

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Tracciabilità operazioni</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="audit-logs-title">{t("audit_logs.title")}</h1>
        <p className="mt-2 text-slate-500" data-testid="audit-logs-subtitle">{t("audit_logs.subtitle")}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchLogs} disabled={loading} data-testid="audit-refresh-button"><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={exportCsv} data-testid="audit-export-csv-button"><Download className="h-4 w-4" /> Export CSV</Button>
          <Button variant="outline" onClick={resetFilters} data-testid="audit-reset-filters-button">Reset filtri</Button>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="audit-filters">
        <div className="space-y-1">
          <Label className="text-xs">{t("audit_logs.filter_action")}</Label>
          <Input value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} placeholder="create, update, delete, sign..." data-testid="audit-filter-action" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("audit_logs.filter_entity")}</Label>
          <Input value={filters.entity_type} onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })} placeholder="company, dvr_assessment, risk_assessment..." data-testid="audit-filter-entity" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("audit_logs.filter_user")}</Label>
          <Input value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} placeholder="user_id (uuid)" data-testid="audit-filter-user" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Azienda</Label>
          <Input value={filters.company_id} onChange={(e) => setFilters({ ...filters, company_id: e.target.value })} placeholder="company_id (uuid)" data-testid="audit-filter-company" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Da</Label>
          <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="audit-filter-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">A</Label>
          <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="audit-filter-to" />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="audit-loading">{t("common.loading")}</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="audit-empty">{t("audit_logs.no_logs")}</div>
        ) : (
          <table className="w-full text-sm" data-testid="audit-logs-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">{t("common.date")}</th>
                <th className="px-4 py-3">{t("audit_logs.filter_action")}</th>
                <th className="px-4 py-3">{t("audit_logs.filter_entity")}</th>
                <th className="px-4 py-3">Entity ID</th>
                <th className="px-4 py-3">User ID</th>
                <th className="px-4 py-3">{t("common.company")}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-t border-slate-100" data-testid={`audit-log-row-${log.id}`}>
                  <td className="px-4 py-3 text-slate-600" data-testid={`audit-log-date-${log.id}`}>{fmt(log.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${ACTION_COLORS[log.action] || "bg-slate-100 text-slate-700"}`} data-testid={`audit-log-action-${log.id}`}>{log.action}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700" data-testid={`audit-log-entity-${log.id}`}>{log.entity_type || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500" title={log.entity_id}>{(log.entity_id || "").slice(0, 8)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500" title={log.user_id}>{(log.user_id || "").slice(0, 8)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500" title={log.company_id}>{(log.company_id || "").slice(0, 8) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
